import { useEffect, useState } from 'react'
import { getNodeCameras } from '@/api/nodes'
import type { CameraInfo, CreateCommandRequest } from '@/types'

interface Props {
  nodeId: string
  onSubmit: (req: CreateCommandRequest) => void
  onCancel: () => void
  submitting: boolean
  /** camera_key 초기값 (예: 노드 상세 화면의 빠른 작업에서 전달) */
  initialCameraKey?: string
}

const RUNTIME_DEFAULTS = {
  chunkSeconds: '3600',
  motionThreshold: '500',
}

export default function RecordStartForm({ nodeId, onSubmit, onCancel, submitting, initialCameraKey }: Props) {
  const [cameraKey, setCameraKey] = useState(initialCameraKey ?? '')
  const [fpsOverride, setFpsOverride] = useState<string | null>(null)
  const [chunkSeconds, setChunkSeconds] = useState(RUNTIME_DEFAULTS.chunkSeconds)
  const [motionThreshold, setMotionThreshold] = useState(RUNTIME_DEFAULTS.motionThreshold)
  const [reason, setReason] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})

  const [cameras, setCameras] = useState<CameraInfo[]>([])
  const [camerasLoading, setCamerasLoading] = useState(true)
  const [camerasError, setCamerasError] = useState<string | null>(null)

  useEffect(() => {
    let active = true
    setCamerasLoading(true)
    setCamerasError(null)
    setCameras([])
    setCameraKey(initialCameraKey ?? '')
    setFpsOverride(null)
    getNodeCameras(nodeId)
      .then((res) => {
        if (!active) return
        setCameras(res.items)
        // 초기값이 없으면 첫 번째 카메라를 자동으로 선택한다.
        if (!initialCameraKey && res.items.length > 0) {
          setCameraKey(res.items[0].key)
        }
      })
      .catch(() => {
        if (active) setCamerasError('Failed to load cameras')
      })
      .finally(() => {
        if (active) setCamerasLoading(false)
      })
    return () => { active = false }
  }, [nodeId, initialCameraKey])

  const selectedCamera = cameras.find((c) => c.key === cameraKey)
  // 구버전 서버가 FPS를 생략하면 에이전트 설정을 따르도록 값을 비워 둔다.
  const fps = fpsOverride ?? (selectedCamera?.fps != null ? String(selectedCamera.fps) : '')

  function parseOptionalInt(value: string): number | null {
    const trimmed = value.trim()
    if (!trimmed) return null
    const parsed = Number(trimmed)
    return Number.isInteger(parsed) ? parsed : Number.NaN
  }

  function validate(): boolean {
    const e: Record<string, string> = {}
    if (!cameraKey) {
      e.camera = 'Camera is required'
    }
    const parsedFps = parseOptionalInt(fps)
    if (Number.isNaN(parsedFps) || (parsedFps !== null && (parsedFps < 1 || parsedFps > 30))) {
      e.fps = 'FPS must be an integer from 1 to 30'
    }
    const parsedChunkSeconds = parseOptionalInt(chunkSeconds)
    if (Number.isNaN(parsedChunkSeconds) || (parsedChunkSeconds !== null && parsedChunkSeconds <= 0)) {
      e.chunkSeconds = 'Chunk seconds must be a positive integer'
    }
    const parsedMotionThreshold = parseOptionalInt(motionThreshold)
    if (Number.isNaN(parsedMotionThreshold) || (parsedMotionThreshold !== null && parsedMotionThreshold < 0)) {
      e.motionThreshold = 'Motion threshold must be zero or a positive integer'
    }
    setErrors(e)
    return Object.keys(e).length === 0
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!validate()) return
    const params: Record<string, unknown> = {
      camera_key: cameraKey,
    }
    const parsedFps = parseOptionalInt(fps)
    const parsedChunkSeconds = parseOptionalInt(chunkSeconds)
    const parsedMotionThreshold = parseOptionalInt(motionThreshold)
    if (parsedFps !== null) params.fps = parsedFps
    if (parsedChunkSeconds !== null) params.chunk_seconds = parsedChunkSeconds
    if (parsedMotionThreshold !== null) params.motion_threshold = parsedMotionThreshold
    const trimmedReason = reason.trim()
    onSubmit({
      type: 'recording.start',
      params,
      reason: trimmedReason || undefined,
    })
  }

  const selectedCameraBusy = Boolean(selectedCamera && selectedCamera.recording_status !== 'idle')

  return (
    <form onSubmit={handleSubmit}>
      <div style={fieldStyle}>
        <label htmlFor="recording-camera" style={labelStyle}>
          Camera <span style={{ color: '#c62828' }}>*</span>
        </label>
        {camerasLoading ? (
          <div style={{ fontSize: 12, color: '#888' }}>Loading cameras...</div>
        ) : camerasError ? (
          <div style={{ fontSize: 12, color: '#c62828' }}>{camerasError}</div>
        ) : cameras.length === 0 ? (
          <div style={{ fontSize: 12, color: '#888', padding: '6px 0' }}>
            No cameras configured for this node. Add cameras in the Config editor (cameras).
          </div>
        ) : (
          <>
            <select
              id="recording-camera"
              value={cameraKey}
              onChange={(e) => {
                setCameraKey(e.target.value)
                setFpsOverride(null)
              }}
              style={inputStyle}
            >
              <option value="">-- select camera --</option>
              {cameras.map((cam) => (
                <option key={cam.key} value={cam.key}>
                  {cam.name ? `${cam.name} (${cam.key})` : cam.key}
                  {cam.recording_status !== 'idle' ? ` [${cam.recording_status}]` : ''}
                </option>
              ))}
            </select>
            {selectedCamera?.source_preview && (
              <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                Source: {selectedCamera.source_preview}
              </div>
            )}
            {selectedCameraBusy && (
              <div style={{ fontSize: 11, color: '#e65100', marginTop: 4 }}>
                This camera is not idle. Starting another recording will be rejected.
              </div>
            )}
          </>
        )}
        {errors.camera && <div style={errStyle}>{errors.camera}</div>}
      </div>

      <div style={fieldStyle}>
        <label style={labelStyle}>Recording parameters</label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8 }}>
          <label style={runtimeFieldStyle}>
            <span style={runtimeLabelStyle}>FPS</span>
            <input
              type="number"
              min={1}
              max={30}
              step={1}
              value={fps}
              onChange={(e) => setFpsOverride(e.target.value)}
              placeholder="Use node config"
              disabled={camerasLoading || submitting}
              style={inputStyle}
            />
            {errors.fps && <div style={errStyle}>{errors.fps}</div>}
          </label>
          <label style={runtimeFieldStyle}>
            <span style={runtimeLabelStyle}>Chunk sec</span>
            <input
              type="number"
              min={1}
              step={1}
              value={chunkSeconds}
              onChange={(e) => setChunkSeconds(e.target.value)}
              style={inputStyle}
            />
            {errors.chunkSeconds && <div style={errStyle}>{errors.chunkSeconds}</div>}
          </label>
          <label style={runtimeFieldStyle}>
            <span style={runtimeLabelStyle}>Motion</span>
            <input
              type="number"
              min={0}
              step={1}
              value={motionThreshold}
              onChange={(e) => setMotionThreshold(e.target.value)}
              style={inputStyle}
            />
            {errors.motionThreshold && <div style={errStyle}>{errors.motionThreshold}</div>}
          </label>
        </div>
      </div>

      <div style={fieldStyle}>
        <label htmlFor="recording-reason" style={labelStyle}>Reason (optional)</label>
        <textarea
          id="recording-reason"
          rows={2}
          placeholder="e.g. false alarm debugging on cam-01"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
        />
      </div>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
        <button type="button" onClick={onCancel} style={cancelBtnStyle} disabled={submitting}>
          Cancel
        </button>
        <button
          type="submit"
          style={submitBtnStyle}
          disabled={submitting || camerasLoading || Boolean(camerasError) || !selectedCamera || selectedCameraBusy}
        >
          {submitting ? 'Sending...' : 'Start recording'}
        </button>
      </div>
    </form>
  )
}

const fieldStyle: React.CSSProperties = { marginBottom: 14 }
const labelStyle: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4 }
const runtimeFieldStyle: React.CSSProperties = { minWidth: 0 }
const runtimeLabelStyle: React.CSSProperties = { display: 'block', fontSize: 11, color: '#666', marginBottom: 3 }
const errStyle: React.CSSProperties = { fontSize: 11, color: '#c62828', marginTop: 3 }
const inputStyle: React.CSSProperties = {
  padding: '6px 10px',
  border: '1px solid #d0d0d0',
  borderRadius: 4,
  fontSize: 13,
  fontFamily: 'inherit',
  background: '#fff',
  width: '100%',
  boxSizing: 'border-box',
}
const cancelBtnStyle: React.CSSProperties = {
  padding: '7px 16px',
  background: '#fff',
  border: '1px solid #ccc',
  borderRadius: 4,
  fontSize: 13,
  cursor: 'pointer',
  fontFamily: 'inherit',
}
const submitBtnStyle: React.CSSProperties = {
  padding: '7px 16px',
  background: '#3949ab',
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  fontSize: 13,
  cursor: 'pointer',
  fontFamily: 'inherit',
}
